import type { AccessRight } from "@reporting/rp-types/users";

/**
 * Naming of access rights between the grid and the store.
 *
 * A right is STORED under exactly one name: the bare scope for the access to a module itself
 * (`finance`, `ai`, `admin`) or `scope.type` for a finer right (`finance.read`, `mdm.write`).
 * The grid shows one column per scope and one dropdown item per type, and the bare right appears
 * there as the UI-only type `access`.
 *
 * Everything that compares what the server sent with what a cell holds has to happen in the
 * stored naming. Comparing `finance` (stored) with `finance.access` (composed from the cell) made
 * every edited cell revoke the bare right it still held: the save granted `finance` (a no-op, the
 * row existed) and then revoked it.
 */
export const ACCESS_TYPE = 'access';

/** The grid column of a stored right name. */
export const rightScope = (right: string): string => right.split('.')[0];

/** The dropdown type of a stored right name within its column. */
export const rightType = (right: string): string => right.split('.')[1] ?? ACCESS_TYPE;

/** The stored name of a dropdown item of a scope column. */
export const toStoredRight = (scope: string, type: string): string =>
	type === ACCESS_TYPE ? scope : `${scope}.${type}`;

/** Groups stored right names by scope: scope → dropdown types, the bare right as `access`. */
export function groupRightsByScope(rights: Iterable<string>): Map<string, string[]> {
	const byScope = new Map<string, string[]>();
	for (const right of rights) {
		const scope = rightScope(right);
		if (!byScope.has(scope)) byScope.set(scope, []);
		byScope.get(scope)!.push(rightType(right));
	}
	return byScope;
}

/** The key of one dropdown value item: a serialized tree value `[key, ...]` or a plain key. */
export const itemKey = (item: unknown): string | null => {
	const key = Array.isArray(item) ? item[0] : item;
	return key == null || key === '' ? null : String(key);
};

/**
 * The stored names of the rights a scope cell holds, each once. A type the column does not offer
 * (a pasted text, a stale key) is dropped rather than granted; a cleared cell holds nothing.
 */
export function cellRights(scope: string, value: unknown, allowedTypes: Iterable<string>): string[] {
	const allowed = new Set(allowedTypes);
	const items = value == null ? [] : Array.isArray(value) ? value : [value];
	const rights = new Set<string>();
	for (const item of items) {
		const type = itemKey(item);
		if (type != null && allowed.has(type)) rights.add(toStoredRight(scope, type));
	}
	return [...rights];
}

/** What to grant and what to revoke to get from `original` to `current`, both in stored naming. */
export function diffRights(original: Iterable<string>, current: Iterable<string>): { grant: string[], revoke: string[] } {
	const before = new Set(original);
	const after = new Set(current);
	return {
		grant: [...after].filter(right => !before.has(right)),
		revoke: [...before].filter(right => !after.has(right)),
	};
}

export type RightRef = Pick<AccessRight, 'accounting' | 'id_accounting_group' | 'user_name' | 'id_access_role' | 'right'>;

/** The identity of a stored right row, as the server matches it. */
export const rightKey = (right: RightRef): string => [
	right.accounting ?? '',
	right.id_accounting_group ?? '',
	right.user_name ?? '',
	right.id_access_role ?? '',
	right.right ?? '',
].join('|');

/**
 * The lists as they may be posted: each right once, and a right in both lists in neither. Granting
 * is a no-op on an existing row and revoking is not, so a right that reached both lists would
 * be lost; the intent behind it is "leave it as it is".
 */
export function settleRights<T extends RightRef>(grant: T[], revoke: T[]): { grant: T[], revoke: T[] } {
	const unique = (rights: T[]) => {
		const seen = new Set<string>();
		return rights.filter(right => {
			const key = rightKey(right);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};
	const granted = new Set(grant.map(rightKey));
	const revoked = new Set(revoke.map(rightKey));
	return {
		grant: unique(grant.filter(right => !revoked.has(rightKey(right)))),
		revoke: unique(revoke.filter(right => !granted.has(rightKey(right)))),
	};
}
